import * as React from "react";

// Time-correlated bidirectional scroll-spy between an ordered milestone rail and
// a chronological feed (ADV-1345). One direction is "feed → rail": as the page
// scrolls, the rail highlights the milestone whose time-window the top of the
// viewport currently sits in. The other is "rail → feed": clicking a milestone
// scrolls the feed to the first row at/after that milestone's timestamp.
//
// The correlation is purely temporal — the rail and the feed are independent
// lists (a milestone need not have a 1:1 feed row), so both sides are reduced to
// epoch milliseconds (Date.parse) and matched by "latest milestone whose
// timestamp <= the topmost-visible feed row's occurred_at". This keeps the hook
// decoupled from the feed's row identity: rows are addressed by a stable
// `data-occurred-at` ISO attribute, not by id.
//
// No new deps: a single IntersectionObserver drives the active-id computation
// (no scroll listener, no throttle bookkeeping). prefers-reduced-motion is
// honored for the imperative scroll (auto vs smooth).

/** Minimal ordered milestone shape the spy needs — id + ISO timestamp. */
export interface ScrollSpyMilestone {
  id: string;
  /** ISO timestamp; the milestone list MUST be sorted ascending by this. */
  timestamp: string;
}

interface UseScrollSpyResult {
  /**
   * The milestone whose time-window the top of the scroll viewport is in: the
   * latest milestone with `timestamp <= the topmost visible feed row`. `null`
   * when nothing is resolvable yet (no rows, empty rail, or the viewport sits
   * above the first milestone).
   */
  activeId: string | null;
  /**
   * Scroll the feed so the first row at/after `timestamp` aligns to the top of
   * the scroll container. Falls back to the last row when every row pre-dates
   * the milestone. No-ops when the feed has no addressable rows.
   */
  scrollTo: (timestamp: string) => void;
}

/** Mirror of use-mobile.tsx's matchMedia pattern, scoped to reduced-motion. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

const ROW_SELECTOR = "[data-occurred-at]";

/**
 * Read the addressable feed rows out of a container, in document order (which
 * the feed renders ascending by `occurred_at`, matching the rail). Each row is
 * paired with its parsed epoch so callers never re-parse.
 */
function readRows(
  container: HTMLElement | null,
): { el: HTMLElement; epoch: number }[] {
  if (!container) return [];
  const els = container.querySelectorAll<HTMLElement>(ROW_SELECTOR);
  const rows: { el: HTMLElement; epoch: number }[] = [];
  for (const el of els) {
    const raw = el.getAttribute("data-occurred-at");
    const epoch = raw ? Date.parse(raw) : NaN;
    if (!Number.isNaN(epoch)) rows.push({ el, epoch });
  }
  return rows;
}

/**
 * Time-correlated scroll-spy. `milestones` must be sorted ascending by
 * `timestamp`; `feedRef` must point at the element that contains the rows
 * carrying `data-occurred-at`. The container itself need not be the scroll
 * container — the rows are scrolled into view against whatever ancestor
 * actually scrolls (the app shell's `<main>`), via `scrollIntoView`.
 */
export function useScrollSpy({
  milestones,
  feedRef,
  active = true,
}: {
  milestones: ScrollSpyMilestone[];
  feedRef: React.RefObject<HTMLElement | null>;
  /**
   * Whether the feed is currently mounted. The feed typically lives inside a
   * lazily-mounted tab panel (Radix unmounts inactive `TabsContent`), so on the
   * first render `feedRef.current` is null and the observer has nothing to
   * attach to. Pass the panel's active state here: when it flips true the
   * wiring effect re-runs against the now-mounted rows and arms the spy.
   * Defaults to `true` for callers whose feed is always in the DOM.
   */
  active?: boolean;
}): UseScrollSpyResult {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeId, setActiveId] = React.useState<string | null>(null);

  // Milestone epochs, recomputed only when the list identity changes. Kept in a
  // ref so the observer callback reads the latest set without being re-created.
  const milestoneEpochs = React.useMemo(
    () => milestones.map((m) => ({ id: m.id, epoch: Date.parse(m.timestamp) })),
    [milestones],
  );
  const milestoneEpochsRef = React.useRef(milestoneEpochs);
  milestoneEpochsRef.current = milestoneEpochs;

  // Resolve the active milestone for a given feed-row epoch: the latest
  // milestone whose timestamp is <= the row. We do NOT assume the list is
  // epoch-sorted — the producer sorts lifecycle events by raw-string compare,
  // which only coincides with epoch order while every timestamp is UTC-anchored
  // (true for PostgREST output today, but not a contract). Track the
  // max-qualifying epoch across the whole list instead of early-breaking, so a
  // single mixed-offset timestamp can never misorder the active stage. Returns
  // null when the row pre-dates every milestone.
  const resolveActiveId = React.useCallback(
    (rowEpoch: number): string | null => {
      const list = milestoneEpochsRef.current;
      let found: string | null = null;
      let foundEpoch = -Infinity;
      for (const m of list) {
        if (
          !Number.isNaN(m.epoch) &&
          m.epoch <= rowEpoch &&
          m.epoch >= foundEpoch
        ) {
          found = m.id;
          foundEpoch = m.epoch;
        }
      }
      return found;
    },
    [],
  );

  const scrollTo = React.useCallback(
    (timestamp: string) => {
      const rows = readRows(feedRef.current);
      if (rows.length === 0) return;
      const target = Date.parse(timestamp);
      // First row at/after the milestone; fall back to the last row when every
      // row pre-dates it (the feed window may not reach a late milestone).
      const match =
        rows.find((r) => r.epoch >= target) ?? rows[rows.length - 1];
      match.el.scrollIntoView({
        block: "start",
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
      // Reflect the click in the rail immediately — don't wait for the observer
      // to settle after the smooth scroll.
      setActiveId(
        resolveActiveId(match.epoch) ?? milestoneEpochs[0]?.id ?? null,
      );
    },
    [feedRef, prefersReducedMotion, resolveActiveId, milestoneEpochs],
  );

  // feed → rail: observe every row, and on each change recompute the active
  // milestone from the topmost intersecting row. rootMargin biases the
  // "current" line to ~30% down the viewport so the highlighted milestone is the
  // one the operator is actually reading, not the row scrolling off the top.
  React.useEffect(() => {
    const container = feedRef.current;
    if (!active || !container || milestoneEpochs.length === 0) {
      setActiveId(null);
      return;
    }
    if (typeof IntersectionObserver !== "function") return;

    // Intersection state persists across IO callbacks (which report only the
    // rows that changed) so the topmost-visible row is always resolvable.
    const intersecting = new Map<HTMLElement, boolean>();
    let epochByEl = new Map<HTMLElement, number>();

    const recompute = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        intersecting.set(entry.target as HTMLElement, entry.isIntersecting);
      }
      let topEl: HTMLElement | null = null;
      let topY = Infinity;
      for (const [el, isVisible] of intersecting) {
        if (!isVisible) continue;
        const y = el.getBoundingClientRect().top;
        if (y < topY) {
          topY = y;
          topEl = el;
        }
      }
      if (!topEl) return;
      const epoch = epochByEl.get(topEl);
      if (epoch == null) return;
      setActiveId(resolveActiveId(epoch) ?? milestoneEpochs[0]?.id ?? null);
    };

    const observer = new IntersectionObserver(recompute, {
      root: null,
      rootMargin: "0px 0px -70% 0px",
      threshold: 0,
    });

    // (Re)wire the observer over the current row set. Called once now and again
    // whenever the feed's DOM children change (async load, filter, paging).
    const wire = () => {
      observer.disconnect();
      intersecting.clear();
      const rows = readRows(container);
      epochByEl = new Map(rows.map((r) => [r.el, r.epoch]));
      if (rows.length === 0) {
        setActiveId(null);
        return;
      }
      for (const { el } of rows) observer.observe(el);
    };
    wire();

    // The feed loads asynchronously and can re-render (skeleton → rows); a
    // MutationObserver re-wires the IO over the new rows without a caller-
    // provided row-count signal. No-op when MutationObserver is unavailable.
    let mutation: MutationObserver | undefined;
    if (typeof MutationObserver === "function") {
      mutation = new MutationObserver(wire);
      mutation.observe(container, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      mutation?.disconnect();
    };
    // Re-run when the milestone set changes OR when the feed panel mounts
    // (`active` flips true). feedRef is stable; DOM-row changes within an
    // already-mounted feed are picked up by the MutationObserver above, not by
    // an effect re-run.
  }, [active, feedRef, milestoneEpochs, resolveActiveId]);

  return { activeId, scrollTo };
}
